// Classification, aggregation, and formatting model for the Funded Loans report.
// Extracted from FundedSummaryReport.tsx so that the component file only exports React
// components (keeps react-refresh happy). Shared by the Summary tab, the Details tab, the
// Monthly Production tab, and the Excel export.

import { PipelineLoan } from '../types';

export interface BreakdownRow {
  label: string;
  volume: number;
  units: number;
  pct: number;
}

// --- Label constants (shared between UI and Excel export) ---

export const LOAN_TYPE_LABELS = [
  'Conventional: FNMA/FHLMC',
  'GNMA: FHA',
  'GNMA: VA',
  'GNMA: USDA',
  'Non-Agency Jumbo',
  'Non-QM',
  'Second Liens',
  'Other: CES / HELOC / Portfolio Loans'
] as const;

export const PURPOSE_LABELS = ['Purchase', 'Refinance'] as const;

export const CHANNEL_LABELS = [
  'Retail - Banked',
  'Retail - Brokered',
  'Wholesale (TPO)',
  'Correspondent',
  'Call Center'
] as const;

export const RATE_TYPE_LABELS = ['Fixed', 'ARM'] as const;

// Non-QM doc-type subtypes shown as nested rows under the Non-QM loan-type line.
// 'Other' MUST stay last — buildBreakdown rolls any unmapped category into the final label.
export const NON_QM_SUBTYPE_LABELS = [
  'Bank Statement',
  'Full Doc',
  'DSCR',
  'P&L',
  'Asset Depletion',
  'Asset Utilization',
  'Other'
] as const;

// --- Categorization helpers ---

export function getLoanTypeCategory(loan: PipelineLoan): string {
  // Lien position takes priority — second liens are their own category
  if (loan.lien_position === 'Second Lien') {
return 'Second Liens';
}

  const type = (loan.trans_details_loan_type || '').toLowerCase().trim();
  const program = (loan.rate_lock_loan_program || '').toLowerCase().trim();
  const investor = (loan.investor_name || '').toLowerCase().trim();

  // Program-name check
  if (program.includes('non-qm')) {
return 'Non-QM';
}
  // Investor-based Non-QM routing
  if (investor.includes('vista point')) {
return 'Non-QM';
}
  if (investor.includes('eresi') && !program.includes('agency')) {
return 'Non-QM';
}
  if (investor.includes('luxury')) {
return 'Non-QM';
}
  if (investor.includes('clearedge')) {
return 'Non-QM';
}

  if (type === 'fha') {
return 'GNMA: FHA';
}
  if (type === 'va') {
return 'GNMA: VA';
}
  if (type === 'usda') {
return 'GNMA: USDA';
}
  if (program.includes('jumbo') || program.includes('non-agency')) {
return 'Non-Agency Jumbo';
}
  if (type === 'conventional') {
return 'Conventional: FNMA/FHLMC';
}
  return 'Other: CES / HELOC / Portfolio Loans';
}

export function getPurposeCategory(loan: PipelineLoan): string {
  const purpose = (loan.trans_details_loan_purpose || '').toLowerCase().trim();
  if (purpose.includes('purchase')) {
return 'Purchase';
}
  return 'Refinance';
}

export function getChannelCategory(loan: PipelineLoan): string {
  const ch = (loan.channel || '').toLowerCase().trim();
  if (ch.includes('brokered')) {
return 'Retail - Brokered';
}
  if (ch.includes('retail')) {
return 'Retail - Banked';
}
  if (ch.includes('wholesale')) {
return 'Wholesale (TPO)';
}
  if (ch.includes('correspondent')) {
return 'Correspondent';
}
  return 'Call Center';
}

export function getRateTypeCategory(loan: PipelineLoan): string {
  const amort = (loan.amortization_type || '').toLowerCase().trim();
  if (amort === 'adjustablerate' || amort.includes('arm')) {
return 'ARM';
}
  return 'Fixed';
}

// Classify a Non-QM loan's income-documentation subtype.
// cx_doc_type is authoritative: every live Non-QM loan carries a populated cx_doc_type, and
// keying off it (rather than a regex blending rate_lock_documentation_type) keeps the buckets
// mutually exclusive — e.g. a "Business Bank Statement" loan tagged "FullDocumentation" stays
// Bank Statement instead of double-matching Full Doc. Only when cx_doc_type is blank do we fall
// back to secondary signals (dscr_ratio, then doc-type/program text) with strict ordering.
// Caller is responsible for passing only Non-QM loans.
export function getNonQmSubtype(loan: PipelineLoan): string {
  const doc = (loan.cx_doc_type || '').toLowerCase().trim();

  if (doc.includes('bank statement')) {
    return 'Bank Statement';
  }
  if (doc.startsWith('full doc')) {
    return 'Full Doc';
  }
  if (doc === 'dscr') {
    return 'DSCR';
  }
  if (doc.startsWith('p&l')) {
    return 'P&L';
  }
  if (doc.startsWith('asset depletion')) {
    return 'Asset Depletion';
  }
  if (doc.startsWith('asset utilization')) {
    return 'Asset Utilization';
  }

  // Blank-doc fallback only — never runs when cx_doc_type is present.
  if (!doc) {
    if (typeof loan.dscr_ratio === 'number') {
      return 'DSCR';
    }
    const hay = `${loan.rate_lock_documentation_type || ''} ${loan.rate_lock_loan_program || ''}`
      .toLowerCase()
      .trim();
    if (hay.includes('bank statement') || hay.includes('bankstmt')) {
      return 'Bank Statement';
    }
    if (hay.includes('dscr')) {
      return 'DSCR';
    }
    if (hay.includes('p&l') || hay.includes('profit')) {
      return 'P&L';
    }
    if (hay.includes('asset depletion')) {
      return 'Asset Depletion';
    }
    if (hay.includes('asset utilization')) {
      return 'Asset Utilization';
    }
    // Generic full-doc signal is checked last so specific types win.
    if (hay.includes('full doc') || hay.includes('fulldoc') || hay.includes('full documentation')) {
      return 'Full Doc';
    }
  }

  return 'Other';
}

// --- Aggregation ---

export function getStateCategory(loan: PipelineLoan): string {
  const state = loan.subject_property_state?.trim().toUpperCase() || '';
  return /^[A-Z]{2}$/.test(state) ? state : 'Unknown';
}

/** Rank by the selected period; include comparison-only states without changing that ranking. */
export function getStateLabels(loans: PipelineLoan[], comparisonLoans: PipelineLoan[] = []): string[] {
  const volumes = new Map<string, number>();
  comparisonLoans.forEach((loan) => volumes.set(getStateCategory(loan), 0));
  loans.forEach((loan) => {
    const state = getStateCategory(loan);
    volumes.set(state, (volumes.get(state) || 0) + (loan.trans_details_loan_amt || 0));
  });
  return [...volumes.keys()].sort((a, b) => {
    if (a === 'Unknown') {
      return 1;
    }
    if (b === 'Unknown') {
      return -1;
    }
    return volumes.get(b)! - volumes.get(a)! || a.localeCompare(b);
  });
}

export function buildBreakdown(
  loans: PipelineLoan[],
  categorize: (loan: PipelineLoan) => string,
  orderedLabels: string[]
): BreakdownRow[] {
  const map = new Map<string, { volume: number; units: number }>();
  orderedLabels.forEach((label) => map.set(label, { volume: 0, units: 0 }));

  const fallbackLabel = orderedLabels[orderedLabels.length - 1];
  loans.forEach((loan) => {
    let category = categorize(loan);
    // If categorizer returns unknown label, roll into the last (catch-all) bucket
    if (!map.has(category)) {
category = fallbackLabel;
}
    const existing = map.get(category)!;
    existing.volume += loan.trans_details_loan_amt || 0;
    existing.units += 1;
  });

  const totalVolume = loans.reduce((sum, l) => sum + (l.trans_details_loan_amt || 0), 0);

  return orderedLabels.map((label) => {
    const data = map.get(label) || { volume: 0, units: 0 };
    return {
      label,
      volume: data.volume,
      units: data.units,
      pct: totalVolume > 0 ? (data.volume / totalVolume) * 100 : 0
    };
  });
}

// Non-QM doc-type breakdown — the single source of truth shared by the Summary tab, the
// Monthly Production tab, and both Excel export sheets. Filters to Non-QM, then buckets by
// doc-type subtype. Percentages are share of Non-QM volume (the rows sum to 100% of Non-QM).
// Callers gate display with `rows.some((r) => r.units > 0)` so an empty Non-QM set shows nothing.
export function buildNonQmSubBreakdown(loans: PipelineLoan[]): BreakdownRow[] {
  const nonQm = loans.filter((loan) => getLoanTypeCategory(loan) === 'Non-QM');
  return buildBreakdown(nonQm, getNonQmSubtype, [...NON_QM_SUBTYPE_LABELS]);
}

// --- Formatting ---

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
}

export function formatPct(pct: number): string {
  return `${pct.toFixed(1)}%`;
}
